import Header from "./Header";
import Footer from "./Footer";
import DebugBanner from "./DebugBanner";
import React, { type ReactNode, useEffect, useRef, useState } from "react";
import { Sidebar, Banner, Button } from "@dawa/ui";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  PlusSquare,
  Search,
  User,
  Users,
  FolderGit2,
  History as HistoryIcon,
  Settings,
  CreditCard,
  LogOut,
} from "lucide-react";
import { apiFetch } from "../lib/api";
import { supabase } from "../lib/supabase";
import { useCredits } from "../hooks/useCredits";
import { ShortcutsOverlay } from "./ShortcutsOverlay";

export default function Layout({ children }: { children: ReactNode }) {
  const loc = useLocation();
  const nav = useNavigate();
  const path = loc.pathname;
  const key = "sidebar:collapsed";
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(key) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, collapsed ? "1" : "0");
    } catch {}
  }, [collapsed]);

  const [contentCount, setContentCount] = useState<number | undefined>(undefined);
  const { credits, ready: creditsReady } = useCredits();
  const [isSigningOut, setIsSigningOut] = useState(false);

  async function handleSignOut() {
    if (isSigningOut) return;
    setIsSigningOut(true);
    try {
      await supabase.auth.signOut();
      nav("/auth/login", { replace: true });
    } finally {
      setIsSigningOut(false);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const s = await supabase.auth.getSession();
        const token = s.data.session?.access_token;
        if (!token) return;
        const res = await apiFetch("/v1/contents", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const j = await res.json().catch(() => []);
        if (Array.isArray(j)) setContentCount(j.length);
      } catch {}
    })();
  }, []);
  const mark = (href: string) => path === href || path.startsWith(href + "/");
  const groups = [
    {
      label: "Production",
      items: [
        {
          id: "dashboard",
          label: "Dashboard",
          icon: <LayoutDashboard className="h-4 w-4" />,
          href: "/dashboard",
          active: mark("/dashboard"),
        },
        {
          id: "contents",
          label: "Contenus",
          icon: <FileText className="h-4 w-4" />,
          href: "/contents",
          active: mark("/contents"),
          badge: contentCount,
        },
        {
          id: "new",
          label: "Nouveau",
          icon: <PlusSquare className="h-4 w-4" />,
          href: "/content/new",
          active: mark("/content/new"),
        },
        {
          id: "keywords",
          label: "Mots‑clés",
          icon: <Search className="h-4 w-4" />,
          href: "/keywords",
          active: mark("/keywords"),
        },
      ].map((it) => ({
        ...it,
        onClick: (e: any) => {
          e.preventDefault();
          nav(it.href!);
        },
      })),
    },
    {
      label: "Organisation",
      items: [
        {
          id: "personas",
          label: "Personas",
          icon: <User className="h-4 w-4" />,
          href: "/personas",
          active: mark("/personas"),
        },
        {
          id: "projects",
          label: "Projets",
          icon: <FolderGit2 className="h-4 w-4" />,
          href: "/projects",
          active: mark("/projects"),
        },
        {
          id: "writers",
          label: "Rédacteurs",
          icon: <Users className="h-4 w-4" />,
          href: "/writers",
          active: mark("/writers"),
        },
      ].map((it) => ({
        ...it,
        onClick: (e: any) => {
          e.preventDefault();
          nav(it.href!);
        },
      })),
    },
    {
      label: "Administration",
      items: [
        {
          id: "history",
          label: "Historique",
          icon: <HistoryIcon className="h-4 w-4" />,
          href: "/history",
          active: mark("/history"),
        },
        {
          id: "credits",
          label: "Crédits",
          icon: <CreditCard className="h-4 w-4" />,
          href: "/credits",
          active: mark("/credits"),
          badge: credits,
        },
        {
          id: "billing",
          label: "Facturation",
          icon: <CreditCard className="h-4 w-4" />,
          href: "/billing",
          active: mark("/billing"),
        },
        {
          id: "settings",
          label: "Paramètres",
          icon: <Settings className="h-4 w-4" />,
          href: "/settings",
          active: mark("/settings"),
        },
        {
          id: "logout",
          label: isSigningOut ? "Déconnexion..." : "Se déconnecter",
          icon: <LogOut className="h-4 w-4" />,
          onClick: async (e: any) => {
            e.preventDefault();
            await handleSignOut();
          },
        },
      ].map((it) => ({
        ...it,
        onClick:
          it.id === "logout"
            ? async (e: any) => {
                e.preventDefault();
                await handleSignOut();
              }
            : (e: any) => {
                e.preventDefault();
                nav(it.href!);
              },
      })),
    },
  ];

  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileSidebarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setMobileOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    if (mobileOpen) {
      const container = mobileSidebarRef.current;
      const el = container?.querySelector<HTMLElement>(
        'a, button, [tabindex]:not([tabindex="-1"])',
      );
      el?.focus();
    }
  }, [mobileOpen]);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [hideCreditsBanner, setHideCreditsBanner] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem("banner:credits:dismiss-until");
      return v ? Date.now() < Number(v) : false;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase() || "";
      const editable = (e.target as HTMLElement | null)?.isContentEditable;
      if (tag === "input" || tag === "textarea" || editable) return;
      const k = e.key;
      if (k === "?" || (k === "/" && e.shiftKey)) {
        e.preventDefault();
        setShortcutsOpen(true);
      }
    }
    function onOpenEvent() {
      setShortcutsOpen(true);
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("open:shortcuts", onOpenEvent as any);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("open:shortcuts", onOpenEvent as any);
    };
  }, []);

  // Keyboard shortcuts: g + key to navigate quickly
  const [gotoAwait, setGotoAwait] = useState(false);
  useEffect(() => {
    let t: number | undefined;
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase() || "";
      const editable = (e.target as HTMLElement | null)?.isContentEditable;
      if (tag === "input" || tag === "textarea" || editable) return; // ignore typing fields
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (!gotoAwait && k === "g") {
        setGotoAwait(true);
        window.clearTimeout(t);
        t = window.setTimeout(() => setGotoAwait(false), 1200) as unknown as number;
        e.preventDefault();
        return;
      }
      if (gotoAwait) {
        const map: Record<string, string> = {
          c: "/contents",
          n: "/content/new",
          d: "/dashboard",
          p: "/projects",
          k: "/keywords",
          h: "/history",
          s: "/settings",
          b: "/billing",
        };
        const dest = map[k];
        if (dest) {
          nav(dest);
          setGotoAwait(false);
          window.clearTimeout(t);
          e.preventDefault();
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      if (t) window.clearTimeout(t);
    };
  }, [gotoAwait, nav]);

  return (
    <div className="min-h-screen">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:m-4 focus:rounded focus:bg-white focus:px-2 focus:py-1"
      >
        Aller au contenu
      </a>
      <DebugBanner />
      <div className="flex w-full">
        {/* Desktop sidebar */}
        <div className="hidden md:block">
          <Sidebar
            header={
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-accent" />
                <span className="text-sm font-semibold">Daware</span>
              </div>
            }
            groups={groups}
            collapsed={collapsed}
            onToggle={() => setCollapsed(!collapsed)}
          />
        </div>
        {/* Mobile off-canvas */}
        <div
          className={`fixed inset-0 z-50 md:hidden ${mobileOpen ? "" : "pointer-events-none"}`}
          role="dialog"
          aria-modal="true"
          aria-hidden={!mobileOpen}
        >
          <div
            className={`absolute inset-0 bg-black/30 transition-opacity ${mobileOpen ? "opacity-100" : "opacity-0"}`}
            onClick={() => setMobileOpen(false)}
          />
          <div
            className={`absolute left-0 top-0 h-full w-64 transform bg-surface shadow-xl transition-transform ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
            ref={mobileSidebarRef}
            onKeyDown={(e) => {
              if (e.key !== "Tab") return;
              const container = mobileSidebarRef.current;
              if (!container) return;
              const focusables = container.querySelectorAll(
                'a, button, [tabindex]:not([tabindex="-1"])',
              );
              if (!focusables.length) return;
              const first = focusables[0] as HTMLElement;
              const last = focusables[focusables.length - 1] as HTMLElement;
              if ((e as any).shiftKey) {
                if (document.activeElement === first) {
                  e.preventDefault();
                  last.focus();
                }
              } else {
                if (document.activeElement === last) {
                  e.preventDefault();
                  first.focus();
                }
              }
            }}
          >
            <Sidebar
              header={
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-accent" />
                  <span className="text-sm font-semibold">Daware</span>
                </div>
              }
              groups={groups}
              onNavigate={() => setMobileOpen(false)}
            />
          </div>
        </div>
        <div className="min-h-screen flex-1">
          <Header onToggleSidebar={() => setMobileOpen((v) => !v)} hideNav />
          {/* Global notification banner (e.g., low credits) */}
          {creditsReady && credits <= 2 && !hideCreditsBanner && (
            <div className="px-4 pt-2">
              <Banner
                variant="warning"
                title="Crédits faibles"
                message={`Il vous reste ${credits} crédit${credits > 1 ? "s" : ""}. Pensez à recharger pour éviter les interruptions.`}
                action={
                  <Button size="sm" onClick={() => nav("/credits")}>
                    Acheter des crédits
                  </Button>
                }
                onClose={() => {
                  try {
                    const until = Date.now() + 24 * 60 * 60 * 1000;
                    localStorage.setItem("banner:credits:dismiss-until", String(until));
                  } catch {}
                  setHideCreditsBanner(true);
                }}
              />
            </div>
          )}
          <main id="main-content" className="px-4">
            {children}
          </main>
          <Footer />
        </div>
      </div>
      <ShortcutsOverlay open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </div>
  );
}
